/**
 * Header Component
 *
 * Full-width sticky banner at the top of the app (like Signal).
 * Left: App name + version. Right: user, theme picker, change log, the
 * notification bell, Needs you, Docs, logout.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useApprovalCount } from '../../hooks/useApprovalCount';
import { useTheme, THEMES } from '../../contexts/ThemeContext';
import { Changelog } from '../Changelog';
import { Badge, Button } from '../ui';
// Phase G3 (2026-05-19): DevIdentityPicker no longer renders in the
// header. Admins now preview-as users via /admin/rbac/simulator's
// "Weekly Briefing Preview" tab (Phase G1/G2). The component file +
// /api/dev/identities route are retired in Phase G7's cleanup.
import { Logo } from '../Logo';
import { NotificationBell } from './NotificationBell';

const THEME_LABELS: Record<string, string> = {
  'light':       '☀️ Light',
  'github-dark': '🌑 GitHub Dark',
  'midnight':    '🌃 Midnight Blue',
  'nord':        '❄️ Nord',
  'dracula':     '🧛 Dracula',
  'ocean':       '🌊 Ocean',
  'solarized':   '🌤️ Solarized',
  'rose':        '🌸 Rosé',
  'forest':      '🌲 Forest',
};

export function Header() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [changelogOpen, setChangelogOpen] = useState(false);
  const needsYouCount = useApprovalCount(!!user);

  if (!user) return null;

  const btnStyle = {
    display: 'inline-flex' as const,
    alignItems: 'center' as const,
    gap: 8,
    padding: '6px 12px',
    background: 'var(--bg-tertiary)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer' as const,
    textDecoration: 'none' as const,
    transition: 'all 0.2s',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  };

  return (
    <header
      style={{
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border)',
        padding: '0 clamp(12px, 4vw, 24px)',
        height: 44,
        gap: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: 'var(--shadow)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        flexShrink: 0,
      }}
    >
      {/* Left — app name + version */}
      <Link
        to="/"
        style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, textDecoration: 'none' }}
      >
        <Logo size={24} radius={5} />
        {/* Phone width: the logo alone, so the actions fit (the header never widens a 375px page). */}
        <span
          className="hidden sm:inline"
          style={{
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: '-0.3px',
            color: 'var(--text-primary)',
            whiteSpace: 'nowrap',
          }}
        >
          Compass
        </span>
      </Link>

      {/* Right — user, theme picker, changelog, logout. (Phase G3:
          dev identity dropdown removed — see /admin/rbac/simulator
          "Weekly Briefing Preview" tab for preview-as-user.) */}
      {/* Below the sm breakpoint the name, role, theme and Change Log hide and the rest scrolls
          inside this row instead of widening the page. */}
      <div className="ui-scroll-x" style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, padding: '8px 10px 8px 2px' }}>
        <span className="hidden sm:inline" style={{ color: 'var(--text-secondary)', fontSize: 13, whiteSpace: 'nowrap' }}>
          {user.displayName || user.email}
        </span>

        <span
          className="hidden sm:inline"
          style={{
            background: 'var(--accent-glow)',
            color: 'var(--accent)',
            padding: '4px 12px',
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 600,
            border: '1px solid var(--border)',
          }}
        >
          {user.isAdmin ? 'ADMIN' : 'USER'}
        </span>

        <div className="hidden sm:block" style={{ position: 'relative', flexShrink: 0 }}>
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              background: 'var(--bg-tertiary)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '6px 28px 6px 10px',
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              outline: 'none',
              fontFamily: 'inherit',
            }}
          >
            {THEMES.map((t) => (
              <option key={t.id} value={t.id}>
                {THEME_LABELS[t.id] || t.label}
              </option>
            ))}
          </select>
          <span
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              fontSize: 10,
              pointerEvents: 'none',
            }}
          >
            ▾
          </span>
        </div>

        <span className="hidden sm:inline-flex" style={{ flexShrink: 0 }}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setChangelogOpen(true)}
            style={{ borderRadius: 8, fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            Change Log
          </Button>
        </span>

        {/* The in-app inbox (Phase 2 OMN-01): your own notifications, unread count on the bell.
            Hidden while viewing as someone else: their inbox is theirs alone (the API answers 403). */}
        {!user.impersonating && <NotificationBell />}

        {/* "Needs you" — the approval queue (APR-05). Every request is the signed-in user's own
            (owner-only on the server), so the link is not capability-gated; the badge counts
            pending + failed requests (hooks/useApprovalCount). */}
        <Link
          to="/approvals"
          style={btnStyle}
          data-testid="header-needs-you"
          aria-label={needsYouCount > 0 ? `Needs you: ${needsYouCount} waiting for your approval` : 'Needs you: nothing waiting'}
        >
          Needs you
          {needsYouCount > 0 && (
            <span data-testid="header-needs-you-badge" aria-hidden="true">
              <Badge tone="danger">{needsYouCount > 99 ? '99+' : needsYouCount}</Badge>
            </span>
          )}
        </Link>

        <Link to="/help" style={btnStyle} aria-label="Open the Help Center">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
            <path
              d="M9.2 9.2a2.8 2.8 0 0 1 5.4 1c0 1.8-2.6 2.2-2.6 4"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
            />
            <circle cx="12" cy="17.4" r="1.05" fill="currentColor" />
          </svg>
          Docs
        </Link>

        <a
          href="#"
          onClick={(e) => { e.preventDefault(); logout(); }}
          style={btnStyle}
        >
          Logout
        </a>
      </div>

      <Changelog open={changelogOpen} onClose={() => setChangelogOpen(false)} />
    </header>
  );
}
